import React, { useState, useEffect, useMemo } from 'react';
import Select from 'react-select';
import { useNavigate } from 'react-router-dom';
import { useAuthFetch } from '../hooks/useAuthFetch';
import '../stili/AggiornaGiocatore.css';

const API_URL = process.env.REACT_APP_API_URL;

const RUOLI = ['PORTIERE', 'DIFENSORE', 'CENTROCAMPISTA', 'ATTACCANTE'];
const PIEDI = ['DESTRO', 'SINISTRO', 'AMBIDESTRO'];
const STATI_PAGAMENTO = ['NON_PAGATO', 'PARZIALE', 'PAGATO', 'SCADUTO'];

export default function AggiornaGiocatore() {
  const authFetch = useAuthFetch();
  const navigate = useNavigate();

  const [categorie, setCategorie] = useState([]);
  const [stagioni, setStagioni] = useState([]);

  const [idCategoriaSelezionata, setIdCategoriaSelezionata] = useState('');
  const [statoFiltro, setStatoFiltro] = useState('ATTIVO');

  const [giocatori, setGiocatori] = useState([]);
  const [giocatoreSelezionato, setGiocatoreSelezionato] = useState(null);

  const [formEdit, setFormEdit] = useState({
    nome: '',
    cognome: '',
    codiceFiscale: '',
    dataNascita: '',
    luogoNascita: '',
    numeroTesserino: '',
    piedePreferito: '',
    ruolo: '',
    stato: 'ATTIVO',
    statoPagamento: '',
    idStagione: '',
    idCategoria: '',
    noteSanitarie: ''
  });

  const [message, setMessage] = useState('');

  useEffect(() => {
    const fetchLookup = async () => {
      setMessage('');
      try {
        const resCat = await authFetch(`${API_URL}/api/admin/lookup/categorie`);
        if (!resCat.ok) throw new Error(`Errore fetch categorie (HTTP ${resCat.status})`);
        const dataCat = await resCat.json();
        setCategorie(Array.isArray(dataCat) ? dataCat : []);

        const resStag = await authFetch(`${API_URL}/api/admin/lookup/stagioni`);
        if (!resStag.ok) throw new Error(`Errore fetch stagioni (HTTP ${resStag.status})`);
        const dataStag = await resStag.json();
        setStagioni(Array.isArray(dataStag) ? dataStag : []);
      } catch (err) {
        console.error(err);
        setCategorie([]);
        setStagioni([]);
        setMessage(`❌ ${err.message || 'Errore nel caricamento lookup'}`);
      }
    };

    fetchLookup();
  }, [authFetch]);

  useEffect(() => {
    const fetchGiocatori = async () => {
      setMessage('');
      setGiocatoreSelezionato(null);

      if (!idCategoriaSelezionata || !statoFiltro) {
        setGiocatori([]);
        return;
      }

      try {
        const url =
          `${API_URL}/api/admin/giocatori` +
          `?idCategoria=${encodeURIComponent(idCategoriaSelezionata)}` +
          `&stato=${encodeURIComponent(statoFiltro)}` +
          `&page=0&size=200&sort=cognome&dir=ASC`;

        const res = await authFetch(url);
        if (!res.ok) throw new Error(`Errore fetch giocatori (HTTP ${res.status})`);

        const page = await res.json();
        const list = Array.isArray(page?.content) ? page.content : [];
        setGiocatori(list);
      } catch (err) {
        console.error(err);
        setGiocatori([]);
        setMessage(`❌ ${err.message || 'Errore fetch giocatori'}`);
      }
    };

    fetchGiocatori();
  }, [authFetch, idCategoriaSelezionata, statoFiltro]);

  function nomeCategoria(idCategoria) {
    return categorie.find((c) => String(c.id) === String(idCategoria))?.nome || '';
  }

  function nomeStagione(idStagione) {
    return stagioni.find((s) => String(s.id) === String(idStagione))?.nome || '';
  }

  function handleSelectGiocatore(giocatore) {
    if (!giocatore) {
      setGiocatoreSelezionato(null);
      return;
    }

    setMessage('');
    setGiocatoreSelezionato(giocatore);

    const note = (giocatore.noteSanitarie ?? giocatore.NoteSanitarie ?? '').toString();

    setFormEdit({
      nome: (giocatore.nome || '').toUpperCase(),
      cognome: (giocatore.cognome || '').toUpperCase(),
      codiceFiscale: (giocatore.codiceFiscale || '').toUpperCase(),
      idCategoria: giocatore.idCategoria ? String(giocatore.idCategoria) : '',
      dataNascita: giocatore.dataNascita || '',
      luogoNascita: (giocatore.luogoNascita || '').toUpperCase(),
      numeroTesserino: giocatore.numeroTesserino || '',
      piedePreferito: giocatore.piedePreferito || '',
      ruolo: giocatore.ruolo || '',
      stato: giocatore.stato || 'ATTIVO',
      statoPagamento: giocatore.statoPagamento || '',
      idStagione: giocatore.idStagione ? String(giocatore.idStagione) : '',
      noteSanitarie: note
    });
  }

  function handleEditChange(e) {
    const { name, value } = e.target;

    const rawFields = new Set([
      'dataNascita',
      'numeroTesserino',
      'noteSanitarie',
      'idStagione',
      'idCategoria',
      'piedePreferito',
      'ruolo',
      'stato',
      'statoPagamento'
    ]);

    const upperFields = new Set([
      'nome',
      'cognome',
      'luogoNascita'
    ]);

    if (name === 'codiceFiscale') {
      setFormEdit((prev) => ({
        ...prev,
        codiceFiscale: value.replace(/\s+/g, '').toUpperCase()
      }));
      return;
    }

    if (rawFields.has(name)) {
      setFormEdit((prev) => ({ ...prev, [name]: value }));
      return;
    }

    if (upperFields.has(name)) {
      setFormEdit((prev) => ({ ...prev, [name]: value.toUpperCase() }));
      return;
    }

    setFormEdit((prev) => ({ ...prev, [name]: value }));
  }

  async function ricaricaLista() {
    if (!idCategoriaSelezionata || !statoFiltro) return;

    const url =
      `${API_URL}/api/admin/giocatori` +
      `?idCategoria=${encodeURIComponent(idCategoriaSelezionata)}` +
      `&stato=${encodeURIComponent(statoFiltro)}` +
      `&page=0&size=200&sort=cognome&dir=ASC`;

    const res = await authFetch(url);
    const page = await res.json().catch(() => null);
    const list = Array.isArray(page?.content) ? page.content : [];
    setGiocatori(list);

    if (giocatoreSelezionato) {
      const aggiornato = list.find((g) => g.id === giocatoreSelezionato.id);
      setGiocatoreSelezionato(aggiornato || null);
    }
  }

  async function handleSave() {
    if (!giocatoreSelezionato) return;

    setMessage('');

    const cf = (formEdit.codiceFiscale || '').trim().toUpperCase();
    const cfRegex = /^[A-Z0-9]{16}$/;
    if (!cfRegex.test(cf)) {
      setMessage('❌ Codice fiscale non valido. Deve contenere 16 caratteri (A-Z/0-9).');
      return;
    }

    try {
      const payload = {
        nome: formEdit.nome?.trim().toUpperCase() || '',
        cognome: formEdit.cognome?.trim().toUpperCase() || '',
        codiceFiscale: cf,
        dataNascita: formEdit.dataNascita || null,
        luogoNascita: formEdit.luogoNascita?.trim().toUpperCase() || '',
        numeroTesserino: formEdit.numeroTesserino?.trim() ? formEdit.numeroTesserino.trim() : null,
        idCategoria: formEdit.idCategoria ? Number(formEdit.idCategoria) : null,
        piedePreferito: formEdit.piedePreferito || null,
        ruolo: formEdit.ruolo || null,
        stato: formEdit.stato || 'ATTIVO',
        statoPagamento: formEdit.statoPagamento || null,
        idStagione: formEdit.idStagione ? Number(formEdit.idStagione) : null,
        noteSanitarie: formEdit.noteSanitarie?.trim() ? formEdit.noteSanitarie.trim() : null
      };

      const res = await authFetch(`${API_URL}/api/admin/giocatori/${giocatoreSelezionato.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        const errResp = await res.json().catch(() => ({}));
        throw new Error(errResp.message || `Errore HTTP ${res.status}`);
      }

      const aggiornato = await res.json();

      setGiocatori((prev) => prev.map((g) => (g.id === giocatoreSelezionato.id ? aggiornato : g)));
      handleSelectGiocatore(aggiornato);
      setMessage('✅ Giocatore aggiornato con successo!');
    } catch (err) {
      console.error(err);
      setMessage(`❌ ${err.message || 'Errore nel salvataggio'}`);
    }
  }

  async function handleDisattiva() {
    if (!giocatoreSelezionato) return;

    setMessage('');
    try {
      const res = await authFetch(`${API_URL}/api/admin/giocatori/${giocatoreSelezionato.id}/disattiva`, {
        method: 'PUT'
      });

      if (!res.ok) {
        const errResp = await res.json().catch(() => ({}));
        throw new Error(errResp.message || `Errore HTTP ${res.status}`);
      }

      await ricaricaLista();
      setGiocatoreSelezionato(null);
      setMessage('✅ Giocatore disattivato.');
    } catch (err) {
      console.error(err);
      setMessage(`❌ ${err.message}`);
    }
  }

  async function handleRiattiva() {
    if (!giocatoreSelezionato) return;

    setMessage('');
    try {
      const res = await authFetch(`${API_URL}/api/admin/giocatori/${giocatoreSelezionato.id}/riattiva`, {
        method: 'PUT'
      });

      if (!res.ok) {
        const errResp = await res.json().catch(() => ({}));
        throw new Error(errResp.message || `Errore HTTP ${res.status}`);
      }

      await ricaricaLista();
      setMessage('✅ Giocatore riattivato.');
    } catch (err) {
      console.error(err);
      setMessage(`❌ ${err.message}`);
    }
  }

  const giocatoreOptions = useMemo(() => {
    return [...giocatori]
      .sort((a, b) => `${a.cognome} ${a.nome}`.localeCompare(`${b.cognome} ${b.nome}`))
      .map((g) => ({
        value: String(g.id),
        label: `${g.cognome} ${g.nome} - ${g.codiceFiscale || ''}`,
        giocatore: g
      }));
  }, [giocatori]);

  const selectedGiocatoreOption = useMemo(() => {
    if (!giocatoreSelezionato) return null;
    return (
      giocatoreOptions.find((opt) => String(opt.value) === String(giocatoreSelezionato.id)) || null
    );
  }, [giocatoreOptions, giocatoreSelezionato]);

  const selectStyles = {
    control: (base) => ({
      ...base,
      minHeight: 46,
      borderRadius: 11,
      border: 'none',
      boxShadow: 'none',
      backgroundColor: '#ffffff',
      '&:hover': {
        border: 'none'
      }
    }),
    valueContainer: (base) => ({
      ...base,
      padding: '0 12px'
    }),
    placeholder: (base) => ({
      ...base,
      color: '#777'
    }),
    singleValue: (base) => ({
      ...base,
      color: '#222'
    }),
    input: (base) => ({
      ...base,
      color: '#222'
    }),
    indicatorSeparator: (base) => ({
      ...base,
      display: 'none'
    }),
    menu: (base) => ({
      ...base,
      borderRadius: 12,
      overflow: 'hidden',
      zIndex: 30
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isFocused ? 'rgba(53, 6, 209, 0.08)' : '#ffffff',
      color: '#222',
      cursor: 'pointer'
    })
  };

  return (
    <div className="agg-giocatore-container" translate="no">
      <h2 className="agg-giocatore-title">Aggiorna Giocatore</h2>

      {message && (
        <div
          className={`agg-giocatore-message-card ${
            message.startsWith('✅') ? 'agg-giocatore-success-message' : 'agg-giocatore-error-message'
          }`}
        >
          {message}
        </div>
      )}

      <div className="agg-giocatore-filtri-box">
        <div className="agg-giocatore-filtri-grid agg-giocatore-filtri-grid-3">
          <div className="agg-giocatore-form-group">
            <label className="agg-giocatore-label" htmlFor="idCategoriaSelezionata">
              Categoria
            </label>
            <select
              className="agg-giocatore-select"
              id="idCategoriaSelezionata"
              value={idCategoriaSelezionata}
              onChange={(e) => setIdCategoriaSelezionata(e.target.value)}
              required
            >
              <option value="">Seleziona una categoria</option>
              {categorie.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.nome}
                </option>
              ))}
            </select>
          </div>

          <div className="agg-giocatore-form-group">
            <label className="agg-giocatore-label" htmlFor="statoFiltro">
              Stato
            </label>
            <select
              className="agg-giocatore-select"
              id="statoFiltro"
              value={statoFiltro}
              onChange={(e) => setStatoFiltro(e.target.value)}
            >
              <option value="ATTIVO">ATTIVO</option>
              <option value="INATTIVO">INATTIVO</option>
            </select>
          </div>

          <div className="agg-giocatore-form-group">
            <label className="agg-giocatore-label">Giocatore</label>
            <div className="agg-giocatore-select-shell">
              <Select
                classNamePrefix="agg-giocatore-react-select"
                options={giocatoreOptions}
                value={selectedGiocatoreOption}
                onChange={(selectedOption) =>
                  handleSelectGiocatore(selectedOption ? selectedOption.giocatore : null)
                }
                placeholder="Cerca e seleziona un giocatore..."
                isClearable
                isSearchable
                noOptionsMessage={() => 'Nessun giocatore trovato'}
                styles={selectStyles}
              />
            </div>
          </div>
        </div>
      </div>

      {giocatoreSelezionato && (
        <div className="agg-giocatore-card-modifica">
          <div className="agg-giocatore-sezione">
            <h3 className="agg-giocatore-sezione-titolo">Contesto giocatore</h3>

            <div className="agg-giocatore-grid-2">
              <div className="agg-giocatore-form-group">
                <label className="agg-giocatore-label">Categoria</label>
                <select
                  className="agg-giocatore-select"
                  name="idCategoria"
                  value={formEdit.idCategoria || ''}
                  onChange={handleEditChange}
                >
                  <option value="">-- Categoria --</option>
                  {categorie.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.nome}
                    </option>
                  ))}
                </select>
              </div>

              <div className="agg-giocatore-form-group">
                <label className="agg-giocatore-label">Stagione</label>
                <select
                  className="agg-giocatore-select"
                  name="idStagione"
                  value={formEdit.idStagione || ''}
                  onChange={handleEditChange}
                >
                  <option value="">-- Seleziona stagione --</option>
                  {stagioni.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.nome}
                      {s.attiva ? ' (ATTIVA)' : ''}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="agg-giocatore-sezione">
            <h3 className="agg-giocatore-sezione-titolo">Dati giocatore</h3>

            <div className="agg-giocatore-grid-2">
              <div className="agg-giocatore-form-group">
                <label className="agg-giocatore-label">Nome</label>
                <input
                  className="agg-giocatore-input uppercase"
                  name="nome"
                  value={formEdit.nome || ''}
                  onChange={handleEditChange}
                />
              </div>

              <div className="agg-giocatore-form-group">
                <label className="agg-giocatore-label">Cognome</label>
                <input
                  className="agg-giocatore-input uppercase"
                  name="cognome"
                  value={formEdit.cognome || ''}
                  onChange={handleEditChange}
                />
              </div>

              <div className="agg-giocatore-form-group">
                <label className="agg-giocatore-label">Codice Fiscale</label>
                <input
                  className="agg-giocatore-input uppercase"
                  name="codiceFiscale"
                  value={formEdit.codiceFiscale || ''}
                  onChange={handleEditChange}
                  maxLength={16}
                />
              </div>

              <div className="agg-giocatore-form-group">
                <label className="agg-giocatore-label">Data di nascita</label>
                <input
                  className="agg-giocatore-input"
                  type="date"
                  name="dataNascita"
                  value={formEdit.dataNascita || ''}
                  onChange={handleEditChange}
                />
              </div>

              <div className="agg-giocatore-form-group">
                <label className="agg-giocatore-label">Luogo di nascita</label>
                <input
                  className="agg-giocatore-input uppercase"
                  name="luogoNascita"
                  value={formEdit.luogoNascita || ''}
                  onChange={handleEditChange}
                />
              </div>

              <div className="agg-giocatore-form-group">
                <label className="agg-giocatore-label">Numero tesserino</label>
                <input
                  className="agg-giocatore-input"
                  name="numeroTesserino"
                  value={formEdit.numeroTesserino || ''}
                  onChange={handleEditChange}
                />
              </div>

              <div className="agg-giocatore-form-group">
                <label className="agg-giocatore-label">Piede preferito</label>
                <select
                  className="agg-giocatore-select"
                  name="piedePreferito"
                  value={formEdit.piedePreferito || ''}
                  onChange={handleEditChange}
                >
                  <option value="">-- Seleziona --</option>
                  {PIEDI.map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </select>
              </div>

              <div className="agg-giocatore-form-group">
                <label className="agg-giocatore-label">Ruolo</label>
                <select
                  className="agg-giocatore-select"
                  name="ruolo"
                  value={formEdit.ruolo || ''}
                  onChange={handleEditChange}
                >
                  <option value="">-- Seleziona --</option>
                  {RUOLI.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
              </div>

              <div className="agg-giocatore-form-group">
                <label className="agg-giocatore-label">Stato pagamento</label>
                <select
                  className="agg-giocatore-select"
                  name="statoPagamento"
                  value={formEdit.statoPagamento || ''}
                  onChange={handleEditChange}
                >
                  <option value="">-- Seleziona --</option>
                  {STATI_PAGAMENTO.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>

              <div className="agg-giocatore-form-group">
                <label className="agg-giocatore-label">Stato</label>
                <input
                  className="agg-giocatore-input"
                  value={giocatoreSelezionato.stato || ''}
                  disabled
                />
              </div>

              <div className="agg-giocatore-form-group agg-giocatore-full-width">
                <label className="agg-giocatore-label">Note sanitarie</label>
                <textarea
                  className="agg-giocatore-textarea"
                  name="noteSanitarie"
                  value={formEdit.noteSanitarie || ''}
                  onChange={handleEditChange}
                />
              </div>
            </div>

            <div className="agg-giocatore-info-riepilogo">
              <span>
                <strong>Categoria attuale:</strong> {nomeCategoria(giocatoreSelezionato.idCategoria)}
              </span>
              <span>
                <strong>Stagione attuale:</strong> {nomeStagione(giocatoreSelezionato.idStagione)}
              </span>
            </div>

            <div className="agg-giocatore-bottoni-espansi">
              <button className="agg-giocatore-salva-btn" type="button" onClick={handleSave}>
                Salva
              </button>

              <button
                className="agg-giocatore-annulla-btn"
                type="button"
                onClick={() => setGiocatoreSelezionato(null)}
              >
                Annulla
              </button>

              {String(giocatoreSelezionato.stato).toUpperCase() === 'ATTIVO' ? (
                <button
                  className="agg-giocatore-disattiva-btn"
                  type="button"
                  onClick={handleDisattiva}
                >
                  Disattiva
                </button>
              ) : (
                <button
                  className="agg-giocatore-riattiva-btn"
                  type="button"
                  onClick={handleRiattiva}
                >
                  Riattiva
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="agg-giocatore-button-row">
        <button
          className="agg-giocatore-custom-button"
          type="button"
          onClick={() => navigate('/gestionale')}
        >
          Torna al Gestionale
        </button>
      </div>
    </div>
  );
}